import {
  ChangeEvent,
  DetailedHTMLProps,
  FocusEvent,
  ForwardedRef,
  InputHTMLAttributes,
  ReactNode,
  SyntheticEvent,
  forwardRef,
  useEffect,
  useState,
} from 'react'
import { isIOS } from 'react-device-detect'

import { TypeMargin, TypeSize } from '@/core/types'
import { cn } from '@bem-react/classname'
import { v4 as uuidv4 } from 'uuid'

import Icon from '@/components/UI/Icon'
import { Message } from '@/components/UI/Message/Message'

import './Field.scss'

const cnField = cn('Field')

export type FieldType = 'text' | 'number' | 'password' | 'email' | 'tel'

export interface IField
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  uid?: string
  type?: FieldType
  label?: string
  name: string
  errors?: string
  placeholder?: string
  classNameInput?: string
  classNameLabel?: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  iconLeft?: ReactNode | string
  iconRight?: ReactNode | string
  noArrow?: boolean
  returnValue?: boolean
  isBackgroundSquare?: boolean
  // Зачем нижнее значение?
  backgroundSquare?: string
  handleOnBlur?: (e?: SyntheticEvent) => void
  handleCustomChange?: (
    e?: ChangeEvent<HTMLInputElement> | number | string,
  ) => void
  pattern?: string
  isNumber?: boolean
  sizeInput?: TypeSize
  mb?: TypeMargin
  info?: string
}

export const Field = forwardRef(
  (
    {
      uid,
      type = 'text',
      value,
      label,
      onChange,
      onBlur,
      name,
      required,
      errors,
      placeholder,
      readonly = false,
      className,
      classNameLabel,
      classNameInput,
      disabled,
      iconLeft,
      iconRight,
      noArrow,
      handleOnBlur,
      returnValue,
      handleCustomChange,
      children,
      pattern,
      isNumber,
      sizeInput = 'md',
      mb = 'md',
      onFocus,
      ...props
    }: IField,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [patternLocal, setPatternLocal] = useState<string | undefined>(
      undefined,
    )
    const [initType, setInitType] = useState<FieldType>('text')
    const [focused, setFocused] = useState<boolean>(false)

    useEffect(() => {
      setInitType(type)
    }, [type])

    useEffect(() => {
      const p = '^-?[0-9]\\d*\\.?\\d*$'
      if (isNumber) setPatternLocal(p)
      if (pattern) setPatternLocal(pattern)
    }, [isNumber, pattern])

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const inputValue = e.target.value
      if (onChange) {
        onChange(e)
      } else if (handleCustomChange) {
        handleCustomChange(returnValue ? inputValue : e)
      }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
      setFocused(false)

      if (type === 'password' && onBlur) onBlur(e)
    }

    const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
      setFocused(true)
      onFocus && onFocus(e)
    }

    const changeTypeInput = (): void => {
      setInitType(initType === 'password' ? 'text' : 'password')
    }

    return (
      <div
        className={cnField(
          {
            xxl: mb === 'xxl',
            xl: mb === 'xl',
            lg: mb === 'lg',
            md: mb === 'md',
            sm: mb === 'sm',
            none: mb === 'none',
          },
          [className],
        )}
      >
        <div className={cnField('wrap')}>
          {label && (
            <label
              htmlFor={uid ? uid : 'input-' + uuidv4()}
              className={cnField(
                'label',
                {
                  required,
                  error: !!errors,
                },
                [classNameLabel],
              )}
            >
              {label}
            </label>
          )}
          <div className={cnField('center')}>
            {iconLeft && (
              <div
                className={cnField('icon', {
                  left: true,
                  valid: !errors,
                  error: !!errors,
                })}
              >
                {iconLeft}
              </div>
            )}
            <input
              placeholder={placeholder}
              id={uid ? uid : 'input-' + uuidv4()}
              value={value}
              name={name}
              type={initType}
              onChange={handleChange}
              onBlur={e => {
                handleBlur(e)
                handleOnBlur && handleOnBlur(e)
              }}
              onFocus={handleFocus}
              disabled={disabled}
              readOnly={readonly}
              className={cnField(
                'input',
                {
                  error: !!errors,
                  arrow: noArrow,
                  xxl: sizeInput === 'xxl',
                  xl: sizeInput === 'xl',
                  lg: sizeInput === 'lg',
                  md: sizeInput === 'md',
                  sm: sizeInput === 'sm',
                  ios: isIOS,
                  valid: !errors,
                  focused,
                },
                [classNameInput],
              )}
              ref={ref}
              pattern={patternLocal}
              {...props}
            />

            {type === 'password' ? (
              <button
                type='button'
                className={cnField('icon', {
                  right: true,
                })}
                onClick={changeTypeInput}
              >
                <Icon
                  name={initType === 'password' ? 'eye' : 'eye-view'}
                  fill='var(--color-gray-700)'
                  width={18}
                  height={18}
                />
              </button>
            ) : (
              <>
                {iconRight && !children && (
                  <div
                    className={cnField('icon', {
                      right: true,
                    })}
                  >
                    {iconRight}
                  </div>
                )}
              </>
            )}

            {children && children}
          </div>
        </div>

        <Message
          className={cnField('error')}
          text={errors}
          type='error'
          open={!!errors}
        />
      </div>
    )
  },
)
